import { useState } from 'react';

import { Close } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';

import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';
import EmailPreviewContent from 'domains/content/components/EmailPreviewContent/EmailPreviewContent';
import { isTemplate } from 'domains/content/types';
import Button from 'domains/core/components/Button';
import useSenderProfile from 'hooks/queries/useSenderProfile';
import useTemplate from 'hooks/queries/useTemplate';

import { CampaignStateTypes } from '../../customCampaignReducer';

const previewContentBase = {
    height: '100%',
    direction: 'column',
    overflow: 'hidden',
};

type Props = {
    campaignState: CampaignStateTypes;
    onClose: () => void;
};

const ABTestEmailPreview = ({ campaignState, onClose }: Props) => {
    const [selectedTab, setSelectedTab] = useState<0 | 1>(0);

    const emailA = campaignState.sends[0] as any;
    const emailB = campaignState.sends[1] as any;

    const selectedEmail = selectedTab === 0 ? emailA : emailB;
    const templateId = selectedEmail?.templateId;
    const senderProfileId = selectedEmail?.senderProfileId;

    // Fetch data for the selected email
    const { data: previewTemplate } = useTemplate({
        templateId: `${templateId}`,
        reactQueryOptions: {
            enabled: !!templateId,
        },
    });

    const { data: emailSenderProfile } = useSenderProfile(senderProfileId, {
        enabled: !!senderProfileId,
    });

    return (
        <Stack bgcolor="#fff" height="100%" width="100%" zIndex={100}>
            {/* Header with tabs and close button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5} px={1}>
                {/* Tabs */}
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* <Typography variant="buttonSmall" color="action.active">
                        content preview
                    </Typography> */}

                    {/* Tab A */}
                    <Box
                        data-testid="template-switch-a"
                        onClick={() => setSelectedTab(0)}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '16px',
                            paddingRight: '8px',
                            paddingY: '8px',
                            borderRadius: '0px',
                            borderBottom: selectedTab === 0 ? '2px solid' : '2px solid transparent',
                            borderBottomColor: selectedTab === 0 ? 'primary.main' : 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <ABTestLabelBox label="A" size="small" />
                    </Box>

                    {/* Tab B */}
                    <Box
                        data-testid="template-switch-b"
                        onClick={() => setSelectedTab(1)}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '16px',
                            paddingRight: '8px',
                            paddingY: '8px',
                            borderRadius: '0px',
                            borderBottom: selectedTab === 1 ? '2px solid' : '2px solid transparent',
                            borderBottomColor: selectedTab === 1 ? 'primary.main' : 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <ABTestLabelBox label="B" size="small" />
                    </Box>
                </Stack>

                {/* Close button */}
                <Button variant="text" size="small" endIcon={<Close />} onClick={onClose}>
                    close preview
                </Button>
            </Stack>

            {/* Preview content */}
            <Stack sx={previewContentBase}>
                <Stack overflow="auto">
                    <EmailPreviewContent
                        isOpen={true}
                        onClose={onClose}
                        emailTemplateHTML={isTemplate(previewTemplate) && previewTemplate.html}
                        fromAddress={emailSenderProfile?.from_address}
                        isCloseEnabled={false}
                        isGridItem={false}
                        preHeader={selectedEmail?.preHeader}
                        subjectLine={selectedEmail?.subjectLine}
                        sx={{ border: 'none', boxShadow: 'none', height: 'auto' }}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default ABTestEmailPreview;
